import {
	ActionListItem,
	Dialog,
	ActionListSubMenuHeader,
	Toolbar
} from "client/Components";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	ContactAddress,
	createPersonalContact,
	ExchangeContactDetailsModel,
	getPersonalContactDetails,
	SaveContactModel,
	updatePersonalContact
} from "client/Api/Email/contacts";
import { EditForm } from "./EditForm";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isEmailAddress,
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import styles from "./EditDialog.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

const validation: IValidationConfiguration<SaveContactModel> = createValidationConfiguration<SaveContactModel>();
validation.forScalar("firstName")
	.add(isNotNullNorUndefined(), isNotEmpty());

validation.forScalar("emailAddress")
	.add(isEmailAddress());

validation.forScalar("emailAddress2")
	.add(isEmailAddress());

validation.forScalar("emailAddress3")
	.add(isEmailAddress());

validation.forScalar("birthday")
	.add(isValidDate());

export type EditDialogProps = {
	onSave(contactId: string): void;
	onClose(): void;
	exchangeContactId?: string;
};

export const EditDialog = (props: EditDialogProps): ReactElement => {
	const {
		onSave,
		onClose: onCloseFromProps,
		exchangeContactId
	} = props;

	const [model, setModel] = useState<SaveContactModel>(getNewSaveModel());

	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);
	const formHandler: IFormHandler<SaveContactModel> = useFormHandler(model, setModel, validation);
	const [addMoreMenuView, setAddMoreMenuView] = useState<AddMoreMenuView>("main-menu");
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const [isBusy, setIsBusy] = useState<boolean>();

	const [visibilityState, setVisibilityState] = useState<ContactFieldVisibilityState>(getVisibilityState(model));

	useEffect(
		() => {
			if (!exchangeContactId) {
				setModel(getNewSaveModel());
				return;
			}

			async function fetchContact(contactId: string, signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const contact: ExchangeContactDetailsModel = await getPersonalContactDetails(contactId, signal);
					const saveModel: SaveContactModel = convertToSaveModel(contact);
					setModel(saveModel);
					setVisibilityState(
						getVisibilityState(saveModel)
					);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			}

			const cancellation = new AbortController();
			fetchContact(exchangeContactId, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			exchangeContactId,
			onClose
		]
	);

	const submitHandler = async (): Promise<void> => {
		try {
			setIsBusy(true);
			const validationResult: InputValidationResult = await formHandler.validation.validate(globalCancellation);
			if (validationResult.kind === InputValidationResultKind.Invalid) {
				setIsBusy(false);
				return;
			}

			const modelWithAdjustedBirthday = {
				...model,
				birthday: adjustTimeZoneToUTC(model.birthday)
			};

			let savedContactId: string;
			if (exchangeContactId) {
				await updatePersonalContact(exchangeContactId, modelWithAdjustedBirthday, globalCancellation);
				savedContactId = exchangeContactId;
			} else {
				savedContactId = await createPersonalContact(modelWithAdjustedBirthday, globalCancellation);
				alertManager.showSuccess(localize("_SuccessfullyCreatedTheContact"));
			}
			setIsBusy(false);
			onSave(savedContactId);
		} catch(e) {
			if (isCancelledError(e)) {
				return;
			}
			setIsBusy(false);
			if (formHandler.validation.tryProcessServerError(e)) {
				return;
			}

			alertManager.showError(localize(exchangeContactId ? "_FailedToEditTheContact" : "_FailedToCreateTheContact"));
			console.error(e);
		}
	};

	const canAddAnyMoreField: boolean = Boolean(
		getFirstInvisibleField(visibilityState, allVisibilityStateFields)
	);

	const renderAddMoreMenuItems = (): ReactElement => {
		switch (addMoreMenuView) {
			case "main-menu":
				return (
					<AddMoreMainMenuItems
						fieldVisibility={visibilityState}
						onFieldVisibilityChanged={setVisibilityState}
						onOpenPhoneMenu={() => setAddMoreMenuView("phone-menu")}
						onOpenAddressMenu={() => setAddMoreMenuView("address-menu")}
						onOpenOtherMenu={() => setAddMoreMenuView("other-menu")}
					/>
				);
			case "phone-menu":
				return (
					<AddMorePhoneMenuItems
						fieldVisibility={visibilityState}
						onFieldVisibilityChanged={setVisibilityState}
						onGoBack={() => setAddMoreMenuView("main-menu")}
					/>
				);
			case "address-menu":
				return (
					<AddMoreAddressMenuItems
						fieldVisibility={visibilityState}
						onFieldVisibilityChanged={setVisibilityState}
						onGoBack={() => setAddMoreMenuView("main-menu")}
					/>
				);
			case "other-menu":
				return (
					<AddMoreOtherMenuItems
						fieldVisibility={visibilityState}
						onFieldVisibilityChanged={setVisibilityState}
						onGoBack={() => setAddMoreMenuView("main-menu")}
					/>
				);
			default:
				throw new UnreachableCaseError(addMoreMenuView);
		}
	};

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="edit-contact-dialog"
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={
					exchangeContactId
						? localize("_EditContact")
						: localize("_NewPersonalContact")
				}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body>
				<EditForm
					formHandler={formHandler}
					onSubmit={submitHandler}
					fieldVisibility={visibilityState}
					timezone={userTimezone}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					{canAddAnyMoreField && (
						<div className={styles.addMoreButtonWrapper}>
							<Toolbar.MenuButton
								icon="plus"
								text={localize("_AddMore")}
								kind="transparent"
								trackingId="add-more-button"
								dropOverWidth={232}
								onDropOverClose={() => setAddMoreMenuView("main-menu")}
							>
								{renderAddMoreMenuItems()}
							</Toolbar.MenuButton>
						</div>
					)}
					<Toolbar.Button
						text={localize(exchangeContactId ? "_SaveAndBack" : "_Save")}
						kind="primary"
						onClick={submitHandler}
						trackingId="save-button"
					/>
					<Toolbar.Button
						text={localize("_Close")}
						kind="secondary"
						onClick={onClose}
						trackingId="close-button"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EditDialog.displayName="EditDialog";

type EditDialogInitialData = Omit<
	EditDialogProps,
	"onSave" | "onClose"
>;

export function useEditContactDialog(): [
	element: ReactElement | null,
	open: (props: EditDialogInitialData) => Promise<TryResult<string>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditDialog
				{...props}
				onSave={onSave}
				onClose={onClose}
			/>
		)
	);
}

function convertToSaveModel(details: ExchangeContactDetailsModel): SaveContactModel {
	return {
		firstName: details.firstName ?? "",
		middleName: details.middleName ?? "",
		lastName: details.lastName ?? "",
		emailAddress: details.emailAddress ?? "",
		emailAddress2: details.emailAddress2 ?? "",
		emailAddress3: details.emailAddress3 ?? "",
		mobilePhone: details.mobilePhone ?? "",
		mobilePhone2: details.mobilePhone2 ?? "",
		homePhone: details.homePhone ?? "",
		homePhone2: details.homePhone2 ?? "",
		businessPhone: details.businessPhone ?? "",
		businessPhone2: details.businessPhone2 ?? "",
		jobTitle: details.jobTitle ?? "",
		department: details.department ?? "",
		company: details.company ?? "",
		homeAddress: {...(details.homeAddress ?? createEmptyAddress())},
		businessAddress: {...(details.businessAddress ?? createEmptyAddress())},
		otherAddress: {...(details.otherAddress ?? createEmptyAddress())},
		birthday: parseDtoDate(details.birthday),
		notes: details.notes ?? ""
	};
}

function getNewSaveModel(): SaveContactModel {
	return {
		firstName: "",
		middleName: "",
		lastName: "",
		emailAddress: "",
		emailAddress2: "",
		emailAddress3: "",
		mobilePhone: "",
		mobilePhone2: "",
		homePhone: "",
		homePhone2: "",
		businessPhone: "",
		businessPhone2: "",
		jobTitle: "",
		department: "",
		company: "",
		homeAddress: createEmptyAddress(),
		businessAddress: createEmptyAddress(),
		otherAddress: createEmptyAddress(),
		birthday: null,
		notes: ""
	};
}

function createEmptyAddress(): ContactAddress {
	return {
		street: "",
		postcode: "",
		city: "",
		state: "",
		country: ""
	};
}

function getVisibilityState(
	model: SaveContactModel
): ContactFieldVisibilityState {
	return {
		emailAddress2: Boolean(model.emailAddress2),
		emailAddress3: Boolean(model.emailAddress3),
		mobilePhone2: Boolean(model.mobilePhone2),
		homePhone: Boolean(model.homePhone),
		homePhone2: Boolean(model.homePhone2),
		businessPhone: Boolean(model.businessPhone),
		businessPhone2: Boolean(model.businessPhone2),
		homeAddress: isAddressNotEmpty(model.homeAddress),
		businessAddress: isAddressNotEmpty(model.businessAddress),
		otherAddress: isAddressNotEmpty(model.otherAddress),
		birthday: Boolean(model.birthday),
		notes: Boolean(model.notes)
	};
};

function isAddressNotEmpty(address: ContactAddress | nil): boolean {
	if (!address) {
		return false;
	}

	const {
		street,
		postcode,
		city,
		state,
		country
	} = address;
	return [
		street,
		postcode,
		city,
		state,
		country
	].some(Boolean);
}

type AddMoreMenuView = "main-menu" | "phone-menu" | "address-menu" | "other-menu";

type AddMoreMainMenuProps = {
	fieldVisibility: ContactFieldVisibilityState;
	onFieldVisibilityChanged(fieldVisibility: ContactFieldVisibilityState): void;
	onOpenPhoneMenu(): void;
	onOpenAddressMenu(): void;
	onOpenOtherMenu(): void;
};

const AddMoreMainMenuItems = (props: AddMoreMainMenuProps): ReactElement => {
	const {
		fieldVisibility,
		onFieldVisibilityChanged,
		onOpenPhoneMenu,
		onOpenAddressMenu,
		onOpenOtherMenu
	} = props;

	const addEmailHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		emailVisibilityStateFields,
		onFieldVisibilityChanged
	);

	const canAddAnyPhone: boolean = Boolean(
		getFirstInvisibleField(
			fieldVisibility,
			allPhoneVisibilityStateFields
		)
	);

	const canAddAnyAddress: boolean = Boolean(
		getFirstInvisibleField(
			fieldVisibility,
			addressVisibilityStateFields
		)
	);

	const canAddOther: boolean = Boolean(
		getFirstInvisibleField(
			fieldVisibility,
			otherVisibilityStateFields
		)
	);

	return (
		<>
			{addEmailHandler !== null && (
				<ActionListItem text={localize("_EmailAddress")} onClick={addEmailHandler} />
			)}
			{canAddAnyPhone && (
				<ActionListItem text={localize("_PhoneNumber")} onClick={onOpenPhoneMenu} subMenuIcon="visible" shouldNotAutoCloseContainingDropOver />
			)}
			{canAddAnyAddress && (
				<ActionListItem text={localize("_Address")} onClick={onOpenAddressMenu} subMenuIcon="visible" shouldNotAutoCloseContainingDropOver />
			)}
			{canAddOther && (
				<ActionListItem text={localize("_Other")} onClick={onOpenOtherMenu} subMenuIcon="visible" shouldNotAutoCloseContainingDropOver />
			)}
		</>
	);
};
AddMoreMainMenuItems.displayName="AddMoreMainMenu";

type AddMorePhoneMenuProps = {
	fieldVisibility: ContactFieldVisibilityState;
	onFieldVisibilityChanged(fieldVisibility: ContactFieldVisibilityState): void;
	onGoBack(): void;
};

const AddMorePhoneMenuItems = (props: AddMorePhoneMenuProps): ReactElement => {
	const {
		fieldVisibility,
		onFieldVisibilityChanged,
		onGoBack
	} = props;

	const addMobilePhoneHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		mobilePhoneVisibilityStateFields,
		onFieldVisibilityChanged
	);

	const addHomePhoneHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		homePhoneVisibilityStateFields,
		onFieldVisibilityChanged
	);

	const addBusinessPhoneHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		businessPhoneVisibilityStateFields,
		onFieldVisibilityChanged
	);

	return (
		<>
			<ActionListSubMenuHeader text={localize("_PhoneNumber")} onGoBack={onGoBack} />
			{addMobilePhoneHandler !== null && (
				<ActionListItem text={localize("_Mobile")} onClick={addMobilePhoneHandler} />
			)}
			{addHomePhoneHandler !== null && (
				<ActionListItem text={localize("_Home")} onClick={addHomePhoneHandler} />
			)}
			{addBusinessPhoneHandler !== null && (
				<ActionListItem text={localize("_Work")} onClick={addBusinessPhoneHandler} />
			)}
		</>
	);
};
AddMorePhoneMenuItems.displayName="AddMorePhoneMenu";

type AddMoreAddressMenuItemsProps = {
	fieldVisibility: ContactFieldVisibilityState;
	onFieldVisibilityChanged(fieldVisibility: ContactFieldVisibilityState): void;
	onGoBack(): void;
};

const AddMoreAddressMenuItems = (props: AddMoreAddressMenuItemsProps): ReactElement => {
	const {
		fieldVisibility,
		onFieldVisibilityChanged,
		onGoBack
	} = props;

	const addHomeAddressHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		["homeAddress"],
		onFieldVisibilityChanged
	);

	const addBusinessAddressHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		["businessAddress"],
		onFieldVisibilityChanged
	);

	const addOtherAddressHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		["otherAddress"],
		onFieldVisibilityChanged
	);

	return (
		<>
			<ActionListSubMenuHeader text={localize("_Address")} onGoBack={onGoBack} />
			{addHomeAddressHandler !== null && (
				<ActionListItem text={localize("_HomeAddress")} onClick={addHomeAddressHandler} />
			)}
			{addBusinessAddressHandler !== null && (
				<ActionListItem text={localize("_WorkAddress")} onClick={addBusinessAddressHandler} />
			)}
			{addOtherAddressHandler !== null && (
				<ActionListItem text={localize("_OtherAddress")} onClick={addOtherAddressHandler} />
			)}
		</>
	);
};
AddMoreAddressMenuItems.displayName="AddMoreAddressMenuItems";

export type AddMoreOtherMenuItemsProps = {
	fieldVisibility: ContactFieldVisibilityState;
	onFieldVisibilityChanged(fieldVisibility: ContactFieldVisibilityState): void;
	onGoBack(): void;
};

export const AddMoreOtherMenuItems = (props: AddMoreOtherMenuItemsProps): ReactElement => {
	const {
		fieldVisibility,
		onFieldVisibilityChanged,
		onGoBack
	} = props;

	const addBirthdayHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		["birthday"],
		onFieldVisibilityChanged
	);

	const addNotesHandler: (() => void) | null = tryGetAddVisibilityHandler(
		fieldVisibility,
		["notes"],
		onFieldVisibilityChanged
	);

	return (
		<>
			<ActionListSubMenuHeader text={localize("_Other")} onGoBack={onGoBack} />
			{addBirthdayHandler !== null && (
				<ActionListItem text={localize("_Birthday")} onClick={addBirthdayHandler} />
			)}
			{addNotesHandler !== null && (
				<ActionListItem text={localize("_Notes")} onClick={addNotesHandler} />
			)}
		</>
	);
};
AddMoreOtherMenuItems.displayName="AddMoreOtherMenuItems";

function tryGetAddVisibilityHandler(
	fieldVisibility: ContactFieldVisibilityState,
	lookupKeys: readonly StringKeyOf<ContactFieldVisibilityState>[],
	onFieldVisibilityChanged: (fieldVisibility: ContactFieldVisibilityState) => void
): (() => void) | null {
	const firstInvisibleKey: StringKeyOf<ContactFieldVisibilityState> | null = getFirstInvisibleField(
		fieldVisibility,
		lookupKeys
	);

	if (!firstInvisibleKey) {
		return null;
	}

	return (): void => {
		onFieldVisibilityChanged({
			...fieldVisibility,
			[firstInvisibleKey]: true
		});
	};
}

function getFirstInvisibleField(
	fieldVisibility: ContactFieldVisibilityState,
	lookupKeys: readonly StringKeyOf<ContactFieldVisibilityState>[]
): StringKeyOf<ContactFieldVisibilityState> | null {
	for (const key of lookupKeys) {
		if (!fieldVisibility[key]) {
			return key;
		}
	}

	return null;
}
export type ContactFieldVisibilityState = {
	emailAddress2: boolean;
	emailAddress3: boolean;
	mobilePhone2: boolean;
	homePhone: boolean;
	homePhone2: boolean;
	businessPhone: boolean;
	businessPhone2: boolean;
	homeAddress: boolean;
	businessAddress: boolean;
	otherAddress: boolean;
	birthday: boolean;
	notes: boolean;
};

const emailVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"emailAddress2",
	"emailAddress3"
];

const mobilePhoneVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"mobilePhone2"
];

const homePhoneVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"homePhone",
	"homePhone2"
];

const businessPhoneVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"businessPhone",
	"businessPhone2"
];

const addressVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"homeAddress",
	"businessAddress",
	"otherAddress"
];

const otherVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	"birthday",
	"notes"
];

const allPhoneVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	...mobilePhoneVisibilityStateFields,
	...homePhoneVisibilityStateFields,
	...businessPhoneVisibilityStateFields
];

const allVisibilityStateFields: readonly StringKeyOf<ContactFieldVisibilityState>[] = [
	...emailVisibilityStateFields,
	...allPhoneVisibilityStateFields,
	...addressVisibilityStateFields,
	...otherVisibilityStateFields
];

function adjustTimeZoneToUTC(date: Date | null): Date | null {
	if (!date) {
		return date;
	}

	// Make sure birthday is in UTC time without time (same behavior as OWA)
	return new Date(
		Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		)
	);
}
